import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { AppName } from '../../apps/definition';
import { SidebarRoutes } from '../../containers/SidebarContainer';
import { trackEvent } from '../../extra/sharedMethods';
import { useQAController } from '../../scripts/QAController';
import { invokeFastApi } from '../../scripts/apis/fastapi';
import { AnalyticsEvent } from '../../scripts/constants/analytics-event';
import { usePromptById } from '../../scripts/hooks/prompts';
import { useSortedInstantApps } from '../../scripts/hooks/sortedInstantApps';
import { logError } from '../../scripts/utils';
import { Loading } from '../controls/Loading/Loading';
import { UIButton } from '../controls/ui/UIButton/UIButton';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';
import { IInnerComponent } from './QAPromptTemplateModal';

export const ViewPromptTemplate: React.FC<IInnerComponent> = ({
  onClose,
  promptId,
}: IInnerComponent) => {
  const { loading, prompt } = usePromptById(promptId);
  const qaController = useQAController();
  const history = useHistory();
  const { sortedInstantConnectedApps } = useSortedInstantApps();

  const handleUseButtonClick = useCallback(
    ({
      template,
      searchFilters,
    }: {
      template: string;
      searchFilters: AppName[];
    }) => {
      if (!promptId) return;
      let baseLlm = false;

      try {
        invokeFastApi({
          path: `/prompts/prompt_used/${promptId}`,
          method: 'POST',
        });

        trackEvent(AnalyticsEvent.WorkflowUsage, { promptId });
      } catch (fetchError) {
        logError(fetchError);
      }

      if (searchFilters[0] === 'all') {
        searchFilters = sortedInstantConnectedApps.map((app) => app.appName);
      }

      if (searchFilters[0] === 'baseLlm') {
        searchFilters = [];
        baseLlm = true;
      }

      history.push({
        pathname: SidebarRoutes.JitQA,
        state: { template, searchFilters, baseLlm },
      });

      qaController.triggerEvent('usePromptTemplate', {
        promptTemplate: template,
        searchFilters,
        baseLlm,
      });
    },
    [history, promptId, sortedInstantConnectedApps, qaController]
  );

  if (loading)
    return (
      <div className="mt-40">
        <Loading />
      </div>
    );

  if (!prompt) return null;

  if (prompt.search_filters[0] === 'all') {
    prompt.search_filters = sortedInstantConnectedApps.map(
      (app) => app.appName
    );
  }

  return (
    <div className="relative h-full">
      <div className="flex flex-col gap-6 max-h-[420px] overflow-y-auto pb-16">
        <div>
          <div className="font-bold">Title</div>
          <div className="font-normal">{prompt.title}</div>
        </div>
        {prompt.description && (
          <div>
            <div className="font-bold">Description</div>
            <div className="font-normal">{prompt.description}</div>
          </div>
        )}
        <div>
          <div className="font-bold">Preview</div>
          <div className="font-normal">{prompt.template}</div>
        </div>
        <div className="flex gap-10">
          <div>
            <div className="font-bold">Created By</div>
            <div className="font-normal">{prompt.author.display_name}</div>
          </div>
          <div>
            {prompt.search_filters.length > 0 &&
              prompt.search_filters[0] !== 'baseLlm' && (
                <>
                  <div className="font-bold">Sources</div>
                  <div className="flex gap-2">
                    {prompt.search_filters.map((filter) => (
                      <UIIcon key={filter} name={filter} type="apps" />
                    ))}
                  </div>
                </>
              )}
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 flex justify-between p-4">
        <UIButton onClick={onClose} type="secondary">
          Cancel
        </UIButton>
        <UIButton
          onClick={() => {
            handleUseButtonClick({
              template: prompt.template,
              searchFilters: prompt.search_filters,
            });
          }}
        >
          Use
        </UIButton>
      </div>
    </div>
  );
};
